import React, { useEffect, useState, useMemo, useCallback } from 'react';
import './Dashboard.css';
import { Doughnut } from 'react-chartjs-2';
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip, } from 'chart.js';
import { useFetch } from "../hooks/useFetch";
import Header from "./common/Header";
import { useTranslation } from 'react-i18next';
import Sidebar from "./common/Sidebar";
import api from "../utils/api";

// Helper: normalize any incoming date string to YYYY-MM-DD (year-month-day)
const toYMD = (input) => {
  if (!input) return '';
  // Try to match at the beginning of the string regardless of having a 'T' or space later
  const m = String(input).match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // Fallback: use Date parsing
  try {
    const d = new Date(input);
    if (!isNaN(d)) {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${mo}-${da}`;
    }
  } catch (e) {}
  return String(input);
};

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Mapa de tipos de tarjeta
const cardTypeMap = {
  0: "Visa",
  1: "Mastercard",
  2: "American Express",
  3: "Débito",
};

// Helpers for tags
const parseTagsCsv = (csv) =>
  (typeof csv === 'string' && csv.length)
    ? csv.split(',').map(s => s.trim()).filter(Boolean)
    : [];

const getTags = (exp) => {
  if (!exp) return [];
  const csv = exp?.data?.tags_csv;
  return typeof csv === 'string' ? parseTagsCsv(csv) : [];
};

// Pastel color per tag (stable by tag name)
const getTagColor = (tag) => {
  if (!tag) return '#e5e7eb';
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0; // force 32-bit
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 70%)`;
};

// Filter tag suggestions excluding already used tags on the expense
const filterTagSuggestions = (query, exclude = [], all = [], opts = { showOnEmpty: true, counts: undefined }) => {
  const { showOnEmpty = true, counts } = opts || {};
  const q = (query || '').trim().toLowerCase();
  const ex = new Set((exclude || []).map(String));
  const base = all.filter(tag => !ex.has(tag));
  const sorted = [...base].sort((a, b) => ((counts?.get(b) || 0) - (counts?.get(a) || 0)) || a.localeCompare(b));
  if (!q) {
    return showOnEmpty ? sorted.slice(0, 8) : [];
  }
  return sorted
    .filter(tag => tag.toLowerCase().includes(q))
    .slice(0, 8);
};

// Stateless dropdown list; parent controls query and onPick
const TagSuggestions = ({ query, exclude = [], all = [], onPick, style, showOnEmpty = true, counts }) => {
  const items = filterTagSuggestions(query, exclude, all, { showOnEmpty, counts });
  if (!items.length) return null;
  return (
    <div
      className="tag-suggestions"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 20,
        marginTop: 4,
        background: 'white',
        border: '1px dashed rgba(0,0,0,0.25)',
        borderRadius: 8,
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
        minWidth: 160,
        maxWidth: 320,
        maxHeight: 220,
        overflowY: 'auto',
        padding: 4,
        ...style,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={(e) => { e.stopPropagation(); onPick && onPick(tag); }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            textAlign: 'left',
            padding: '6px 8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: 6,
          }}
          className="tag-suggestion-item"
        >
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 4,
              height: 12,
              backgroundColor: getTagColor(tag),
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tag}</span>
        </button>
      ))}
    </div>
  );
};

const Expenses = () => {
  const { t, i18n } = useTranslation();
  const [ dashboardData ] = useFetch('/dashboard', {defaultValue: []});
  // useReduxFetch devolvía { data, loading, error } (no transactionsData) y entraba en un
  // bucle de peticiones; useFetch es el hook que usan las demás pantallas.
  const [ expensesData ] = useFetch('/transactions', {defaultValue: null, hasPaging: true, defaultLimit: 1000});
  const transactionsData = useMemo(() => expensesData ?? [], [expensesData]);
  const transactionsLoading = expensesData === null;
  const transactionsError = null;
  const [, setExpenseDataLoaded] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  // Debounce search to avoid filtering on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(id);
  }, [searchQuery]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  // NUEVO: modo de resumen y selección de archivos/tags
  const [summaryMode, setSummaryMode] = useState('month'); // 'month' | 'file' | 'tag'
  const [selectedUploads, setSelectedUploads] = useState([]); // array de claves de upload
  const [selectedTags, setSelectedTags] = useState([]); // array de nombres de tag
  const hasActiveFilters = (
    (selectedMonths && selectedMonths.length > 0) ||
    (selectedCategories && selectedCategories.length > 0) ||
    (selectedUploads && selectedUploads.length > 0) ||
    (selectedTags && selectedTags.length > 0)
  );
  const [ expenses, setExpenses ] = useState([]);
  // Build Tag Summary: total por tag
  const tagSummary = React.useMemo(() => {
    if (!expenses.length) return [];
    const totals = {};
    expenses.forEach(exp => {
      const amt = Number(exp.amount) || 0;
      const tags = getTags(exp);
      if (!tags || tags.length === 0) return;
      tags.forEach(tag => {
        const key = String(tag);
        totals[key] = (totals[key] || 0) + amt;
      });
    });
    return Object.entries(totals).map(([tag, amount]) => ({ tag, amount }));
  }, [expenses]);
  // All unique tags from current expenses (for filter suggestions)
  const allTags = React.useMemo(() => {
    const set = new Set();
    (expenses || []).forEach(exp => getTags(exp).forEach(t => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  // Tag usage frequency map from current expenses
  const tagUsageCount = React.useMemo(() => {
    const map = new Map();
    (expenses || []).forEach(exp => {
      getTags(exp).forEach(t => {
        map.set(t, (map.get(t) || 0) + 1);
      });
    });
    return map;
  }, [expenses]);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedCardType, setSelectedCardType] = useState('');
  const [ newTag, setNewTag ] = useState('');
  const [ addingTagId, setAddingTagId ] = useState(null);

  // Tag add concurrency guard and retry logic
  const inFlightTagAddRef = React.useRef(new Map()); // expenseId -> boolean

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));

  // POST with retries/backoff; treat 409/422 duplicates as success
  const postAddTagWithRetry = async (expenseId, tag) => {
    const key = String(expenseId);
    if (inFlightTagAddRef.current.get(key)) {
      // Prevent overlapping writes for the same expense
      return { ok: false, reason: 'in_flight' };
    }
    inFlightTagAddRef.current.set(key, true);
    try {
      const maxAttempts = 3;
      let attempt = 0;
      let lastErr = null;
      while (attempt < maxAttempts) {
        attempt++;
        try {
          const res = await api.post(`/expense/${expenseId}/tags/add`, { tag });
          return { ok: true, res };
        } catch (e) {
          const status = e?.response?.status;
          if (status === 409 || status === 422) {
            // Tag already exists or validation duplicate — consider success
            return { ok: true, res: e?.response };
          }
          lastErr = e;
          // Retry for network/5xx
          if (!status || (status >= 500 && status < 600)) {
            await sleep(200 * attempt * attempt);
            continue;
          }
          break; // non-retryable (e.g., 400)
        }
      }
      return { ok: false, error: lastErr };
    } finally {
      inFlightTagAddRef.current.set(key, false);
    }
  };

  const addTagToExpense = async (expenseId, tagName) => {
    const tag = (tagName || '').trim();
    // block empty or too long
    if (!tag || tag.length > 64) return;

    // case-insensitive duplication check
    const prevExpense = expenses.find(e => e.id === expenseId);
    const prevTags = getTags(prevExpense);
    const lowerSet = new Set(prevTags.map(t => t.toLowerCase()));
    if (lowerSet.has(tag.toLowerCase())) {
      // already present — just clear inputs and bail as success
      setNewTag('');
      setSelectedExpense(prev => prev && prev.id === expenseId ? { ...prev, newTag: '' } : prev);
      setExpenses(prev => prev.map(exp => exp.id === expenseId ? { ...exp, newTag: '' } : exp));
      return;
    }

    // Helper to apply tags into data.tags_csv only
    const applyTags = (exp, newTags) => ({
      ...exp,
      data: {
        ...(exp.data || {}),
        tags_csv: (newTags || []).join(',')
      }
    });

    // Optimistic UI
    setExpenses(prev => prev.map(exp => {
      if (exp.id !== expenseId) return exp;
      const current = getTags(exp);
      if (current.includes(tag)) return exp;
      const next = [...current, tag];
      const updated = applyTags(exp, next);
      return { ...updated, newTag: '' };
    }));
    setSelectedExpense(prev => {
      if (!prev || prev.id !== expenseId) return prev;
      const current = getTags(prev);
      if (current.includes(tag)) return prev;
      const next = [...current, tag];
      const updated = applyTags(prev, next);
      return { ...updated, newTag: '' };
    });
    setNewTag('');

    // Backend call with retry & duplicate handling
    let saved = false;
    let lastError = null;
    const result = await postAddTagWithRetry(expenseId, tag);
    if (result.ok) {
      try {
        // canonicalize from backend if endpoint exists
        const got = await api.get(`/expense/${expenseId}/tags`);
        const data = got?.data;
        const csv = data?.tags_csv || data?.data?.tags_csv;
        const finalTags = typeof csv === 'string' ? parseTagsCsv(csv) : null;
        if (finalTags) {
          setExpenses(prev => prev.map(exp => exp.id === expenseId ? { ...applyTags(exp, finalTags), newTag: '' } : exp));
          setSelectedExpense(prev => prev && prev.id === expenseId ? { ...applyTags(prev, finalTags), newTag: '' } : prev);
        }
      } catch (_) { /* ignore canonicalization failure */ }
      saved = true;
    } else {
      const e = result.error;
      lastError = e?.response ? `HTTP ${e.response.status}: ${e.response.data || ''}` : (e?.message || String(e));
    }

    if (!saved) {
      setExpenses(prev => prev.map(exp => exp.id === expenseId ? { ...applyTags(exp, prevTags), newTag: '' } : exp));
      setSelectedExpense(prev => prev && prev.id === expenseId ? { ...applyTags(prev, prevTags), newTag: '' } : prev);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[Tags] save failed for', expenseId, 'tag:', tag, 'error:', lastError);
      }
      try { alert(t('dashboard.tagSaveFailed', 'Could not save tag. Please try again.')); } catch(_) {}
    }
  };

  const removeTagFromExpense = async (expenseId, tagName) => {
    const tag = (tagName || '').trim();
    if (!tag) return;

    const prevExpense = expenses.find(e => e.id === expenseId);
    const prevTags = getTags(prevExpense);
    const nextTags = prevTags.filter(tg => tg !== tag);

    const applyTags = (exp, newTags) => ({
      ...exp,
      data: {
        ...(exp.data || {}),
        tags_csv: (newTags || []).join(',')
      }
    });

    // Optimistic UI
    setExpenses(prev => prev.map(exp => exp.id === expenseId ? applyTags(exp, nextTags) : exp));
    setSelectedExpense(prev => prev && prev.id === expenseId ? applyTags(prev, nextTags) : prev);

    let saved = false;
    let lastError = null;
    try {
      // axios allows body in DELETE via the `data` option
      const res = await api.delete(`/expense/${expenseId}/tags`, { data: { tag } });
      const data = res?.data;
      const csv = data?.tags_csv || data?.data?.tags_csv;
      if (typeof csv === 'string') {
        const newTags = parseTagsCsv(csv);
        setExpenses(prev => prev.map(exp => exp.id === expenseId ? applyTags(exp, newTags) : exp));
        setSelectedExpense(prev => prev && prev.id === expenseId ? applyTags(prev, newTags) : prev);
      }
      saved = true;
    } catch (e) {
      lastError = e?.response ? `HTTP ${e.response.status}: ${e.response.data || ''}` : (e?.message || String(e));
    }

    if (!saved) {
      // Revert
      setExpenses(prev => prev.map(exp => exp.id === expenseId ? applyTags(exp, prevTags) : exp));
      setSelectedExpense(prev => prev && prev.id === expenseId ? applyTags(prev, prevTags) : prev);
      // eslint-disable-next-line no-console
      console.error('[Tags] Could not delete tag:', lastError);
      try { alert(t('dashboard.tagDeleteFailed', 'Could not delete tag. Please try again.')); } catch(_) {}
    }
  };

  const [ categoryDataRaw, setCategoryDataRaw ] = useState([]);
  const [ uploadsData ] = useFetch('/uploads', { defaultValue: [] });
  const categoryDataSorted = React.useMemo(() => {
    return Array.isArray(categoryDataRaw)
      ? [...categoryDataRaw].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
      : [];
  }, [categoryDataRaw]);
  const [, setTotalAmount ] = useState(0);
  const [, setTotalTransactions ] = useState(0);
  const [, setRecentUploads ] = useState([]);
  const [ monthlySummaryRaw, setMonthlySummaryRaw ] = useState([]);
  const [ baseMonthlySummaryRaw, setBaseMonthlySummaryRaw ] = useState([]);
  const [ categoryData, setCategoryData ] = useState({});
  const [ categoryColorMap, setCategoryColorMap ] = useState({});
  // Card type filter state
  const [ cardTypes, setCardTypes ] = useState([]);

  // Try to resolve a human filename from various fields (including paths/URLs)
  const resolveFilename = (obj) => {
    if (!obj) return '';
    const tryBasename = (val) => {
      const s = String(val);
      const parts = s.split(/[\\/]/);
      return parts[parts.length - 1] || '';
    };
    const candidates = [
      obj.filename, obj.file_name, obj.name, obj.original_name, obj.originalName,
      obj.stored_name, obj.storedName, obj.file, obj.path, obj.url, obj.link
    ].filter(Boolean);
    for (const c of candidates) {
      const base = tryBasename(c);
      if (base) return base;
    }
    // Heuristic: scan any string field that looks like a filename or contains a dot/extension
    try {
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v !== 'string') continue;
        if (/name|file|path|url|link/i.test(k) || /\.(pdf|csv|xlsx|xls|ofx|txt|json)$/i.test(v)) {
          const base = tryBasename(v);
          if (base) return base;
        }
      }
    } catch {}
    return '';
  };

  const uploadsMap = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(uploadsData?.uploads)
      ? uploadsData.uploads
      : Array.isArray(uploadsData?.recent_uploads)
        ? uploadsData.recent_uploads
        : Array.isArray(uploadsData)
          ? uploadsData
          : [];
    list.forEach((u) => {
      if (!u) return;
      const id = u.id ?? u.upload_id ?? u.file_id;
      if (id == null) return;
      out.set(String(id), {
        id: String(id),
        filename: resolveFilename(u),
        bank_name: u.bank?.name || u.bank_name || '',
        card_type: u.card_type,
        type: u.type,
      });
      if (u.identifier) {
        out.set(String(u.identifier), {
          id: String(id),
          filename: resolveFilename(u) || u.filename || '',
          bank_name: u.bank?.name || u.bank_name || '',
          card_type: u.card_type,
          type: u.type,
        });
      }
      if (u.filename) {
        out.set(String(u.filename), {
          id: String(id),
          filename: resolveFilename(u) || u.filename || '',
          bank_name: u.bank?.name || u.bank_name || '',
          card_type: u.card_type,
          type: u.type,
        });
      }
    });
    return out;
  }, [uploadsData]);

  const uploadsDashboardMap = useMemo(() => {
    const out = new Map();
    const data = (dashboardData && dashboardData.data) ? dashboardData.data : dashboardData;
    const list = Array.isArray(data?.recent_uploads) ? data.recent_uploads : [];
    list.forEach((u) => {
      if (!u) return;
      const id = u.id ?? u.upload_id ?? u.file_id ?? u.uuid ?? u.guid;
      const filename = resolveFilename(u) || u.filename || u.file_name || u.name || '';
      if (id != null) out.set(String(id), { id: String(id), filename });
      if (filename) out.set(String(filename), { id: id != null ? String(id) : undefined, filename });
      if (u.identifier) out.set(String(u.identifier), { id: id != null ? String(id) : undefined, filename });
    });
    return out;
  }, [dashboardData]);

  // Combined index: prefer explicit uploads, then dashboard recent uploads; index by id AND by filename
  const uploadsIndex = useMemo(() => {
    const out = new Map();
    // from /uploads
    for (const [k, v] of uploadsMap.entries()) out.set(k, v);
    // also index by filename from /uploads
    for (const v of uploadsMap.values()) {
      if (v.filename) out.set(String(v.filename), v);
    }
    // from dashboard recent_uploads (fallback)
    for (const [k, v] of uploadsDashboardMap.entries()) if (!out.has(k)) out.set(k, v);
    return out;
  }, [uploadsMap, uploadsDashboardMap]);

  const getUploadInfo = useCallback((exp) => {
    const embedded = exp.upload || exp.data?.upload || exp.document || exp.data?.document || {};
    const embeddedId = (
      embedded.id ?? embedded.file_id ?? embedded.upload_id ??
      exp.upload?.id ?? exp.upload?.file_id ??
      exp.upload_id ?? exp.expense_upload_id
    );
    const embeddedIdentifier = embedded.identifier ?? exp.upload_identifier ?? exp.identifier;
    let embeddedFilename = resolveFilename(embedded) || exp.upload_filename || exp.source_document || exp.document_name;
    const bankEmbedded = embedded.bank?.name || embedded.bank_name || exp.upload?.bank_name;
    const cardRawEmbedded = embedded.card_type ?? exp.upload?.card_type;

    let bank = bankEmbedded || '';
    let card = (cardTypeMap[cardRawEmbedded] || cardRawEmbedded || '');
    let file = embeddedFilename || '';
    let key = embeddedId != null
      ? String(embeddedId)
      : (embeddedIdentifier != null ? String(embeddedIdentifier) : (embeddedFilename || ''));

    // Try lookup by id first
    if (embeddedId != null) {
      const hit = uploadsIndex.get(String(embeddedId));
      if (hit) {
        file = hit.filename || file;
        if (hit.bank_name) bank = bank || hit.bank_name;
        if (hit.card_type) card = card || (cardTypeMap[hit.card_type] || hit.card_type || '');
        key = String(hit.id ?? embeddedId);
      }
    }
    // Try lookup by identifier if present
    if ((!file || !file.trim()) && embeddedIdentifier != null) {
      const hitByIdent = uploadsIndex.get(String(embeddedIdentifier));
      if (hitByIdent) {
        file = hitByIdent.filename || file;
        if (hitByIdent.bank_name) bank = bank || hitByIdent.bank_name;
        if (hitByIdent.card_type) card = card || (cardTypeMap[hitByIdent.card_type] || hitByIdent.card_type || '');
        key = String(hitByIdent.id ?? embeddedIdentifier);
      }
    }
    // If still no file, try lookup by filename key
    if ((!file || !file.trim()) && embeddedFilename) {
      const hitByName = uploadsIndex.get(String(embeddedFilename));
      if (hitByName) {
        file = hitByName.filename || file;
        if (hitByName.bank_name) bank = bank || hitByName.bank_name;
        if (hitByName.card_type) card = card || (cardTypeMap[hitByName.card_type] || hitByName.card_type || '');
        key = String((hitByName.id ?? key) || embeddedFilename);
      }
    }

    // Final fallback: if key exists but file is empty, show key instead of generic 'Documento'
    if (!file && key) file = String(key);
    if (!key && file) key = String(file);

    const prefix = [bank, card].filter(Boolean).join(' · ');
    const label = prefix ? `${prefix} — ${file}` : String(file || 'Documento');

    return { key: String(key || ''), label };
  }, [uploadsIndex]);

  // Cache upload info per expense to avoid recomputing on every place
  const expenseUploadInfo = useMemo(() => {
    const m = new Map();
    (expenses || []).forEach((exp) => {
      if (!exp || exp.id == null) return;
      m.set(exp.id, getUploadInfo(exp));
    });
    return m;
  }, [expenses, getUploadInfo]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  // Set dashboard data
  useEffect(() => {
    if (dashboardData.length === 0) {
      return;
    }
    // Unwrap payload if backend returns { data: {...} }
    const data = (dashboardData && dashboardData.data) ? dashboardData.data : dashboardData;
    // For display
    const rawCategories = data?.categories || [];
    const categoryDataRaw = Object.entries(rawCategories)
      .map(([name, amount]) => ({ name, amount: Number(amount) }));
    setCategoryDataRaw(categoryDataRaw);

    // For chart
    const categoryLabels = Object.keys(rawCategories);
    const categoryValues = Object.values(rawCategories);
    const colors = categoryLabels.map(
      (_, i) => `hsl(${(i * 360) / categoryLabels.length}, 50%, 55%)`
    );
    const colorMap = {};
    categoryLabels.forEach((name, i) => {
      colorMap[name] = colors[i];
    });
    setCategoryColorMap(colorMap);
    const categoryData = {
      labels: categoryLabels,
      datasets: [
        {
          data: categoryValues,
          backgroundColor: colors,
        },
      ],
    };
    setCategoryData(categoryData);

    const totalAmount = Number(data.total_expenses);
    setTotalAmount(totalAmount);
    const totalTransactions = Number(data.total_transactions);
    setTotalTransactions(totalTransactions);
    console.log('Recent uploads:', data.recent_uploads);
    const recentUploads = Array.isArray(data.recent_uploads)
      ? data.recent_uploads.map((upload) => upload.filename || 'Sin nombre')
      : [];
    setRecentUploads(recentUploads);

    // Normalize monthly_summary from backend for all cards
    let normalizedMonthly = [];

    // Accept multiple shapes: prefer `monthly_summary`, else `monthly_summary_list`
    const source = (data && data.monthly_summary != null) ? data.monthly_summary : data?.monthly_summary_list;

    // Helper to pad YYYY-M to YYYY-MM
    const padMonth = (m) => {
      const mm = String(m);
      const match = mm.match(/^(\d{4})-(\d{1,2})$/);
      if (match) return `${match[1]}-${match[2].padStart(2, '0')}`;
      return mm;
    };

    const ms = source;

    if (Array.isArray(ms)) {
      if (ms.length && typeof ms[0] === 'object' && ms[0] !== null) {
        if ('month' in ms[0]) {
          normalizedMonthly = ms
            .filter(it => it && typeof it.month === 'string')
            .map(it => ({ month: padMonth(it.month), amount: Number(it.amount) || 0 }));
        } else if (Array.isArray(ms[0])) {
          // e.g., [['2025-1', 12345], ...]
          normalizedMonthly = ms
            .filter(it => Array.isArray(it) && it.length >= 2)
            .map(([m, a]) => ({ month: padMonth(String(m)), amount: Number(a) || 0 }));
        }
      }
    } else if (ms && typeof ms === 'object') {
      const vals = Object.values(ms);
      if (vals.every(v => typeof v === 'number' || typeof v === 'string')) {
        // shape: { 'YYYY-MM': amount }
        normalizedMonthly = Object.entries(ms)
          .map(([m, a]) => ({ month: padMonth(m), amount: Number(a) || 0 }))
          .filter(({ month }) => /^(\d{4})-(\d{2})$/.test(month));
      } else if (vals.every(v => v && typeof v === 'object')) {
        // shape: per-card map; aggregate across cards into YYYY-MM totals
        const monthTotals = {};
        Object.values(ms).forEach(cardMap => {
          Object.entries(cardMap || {}).forEach(([m, a]) => {
            const mm = padMonth(m);
            if (!/^(\d{4})-(\d{2})$/.test(mm)) return;
            monthTotals[mm] = (monthTotals[mm] || 0) + (Number(a) || 0);
          });
        });
        normalizedMonthly = Object.entries(monthTotals).map(([m, a]) => ({ month: m, amount: Number(a) }));
      }
    }

    setBaseMonthlySummaryRaw(normalizedMonthly);
    setMonthlySummaryRaw(normalizedMonthly);
    // Always set the raw summary; filtering will be handled after expenses are loaded
  }, [dashboardData]);
  // Set expenses data
  useEffect(() => {
    if (transactionsData.length === 0) {
      return;
    }
    /*if (expensesData.length === 0) {
      return;
    }*/
    const expenses = Array.isArray(transactionsData.transactions) ?
        transactionsData.transactions :
        Array.isArray(transactionsData) ?
            transactionsData :
            [];
    /*const expenses = Array.isArray(expensesData.transactions)
      ? expensesData.transactions
      : Array.isArray(expensesData)
        ? expensesData
        : [];*/
    setExpenses(expenses);
    // Extract unique card types from expensesData
    const cardTypes = [...new Set(
      expenses.map(e => cardTypeMap[e.upload?.card_type] || e.upload?.card_type).filter(Boolean)
    )];
    setCardTypes(cardTypes);
    setExpenseDataLoaded(true);
  }, [transactionsData]);

  // Build File Summary: totales por documento subido (cartola)
  const fileSummary = useMemo(() => {
    if (!expenses.length) return [];
    const totals = {};
    const labels = {};
    expenses.forEach(exp => {
      const { key, label } = getUploadInfo(exp);
      if (!key) return;
      const amt = Number(exp.amount) || 0;
      totals[key] = (totals[key] || 0) + amt;
      labels[key] = label || String(key);
    });
    const result = Object.entries(totals).map(([key, amount]) => ({ key, label: labels[key], amount }));
    if (result.length === 0 && expenses.length > 0) {
      // Help debug payload shapes in dev
      const sample = expenses[0];
      // eslint-disable-next-line no-console
      console.warn('[FileSummary] No upload keys found. Sample expense keys:', Object.keys(sample || {}));
      // eslint-disable-next-line no-console
      console.warn('[FileSummary] Sample expense.upload:', sample?.upload);
      // eslint-disable-next-line no-console
      console.warn('[FileSummary] Sample expense.data?.upload:', sample?.data?.upload);
      // eslint-disable-next-line no-console
      console.warn('[FileSummary] Sample expense.document:', sample?.document);
    }
    if (result.some(r => !r.label || /Visa|Mastercard|Débito|American Express$/i.test(r.label))) {
      // eslint-disable-next-line no-console
      console.warn('[FileSummary] Some labels may miss filenames. Sample uploadsData keys:', Array.isArray(uploadsData) ? Object.keys(uploadsData[0] || {}) : Object.keys((uploadsData && uploadsData.uploads && uploadsData.uploads[0]) || {}));
    }
    return result;
  }, [expenses, getUploadInfo, uploadsData]);

  // When categories are selected (and we're in file mode), recompute file totals only for those categories
  const fileSummarySource = useMemo(() => {
    if (summaryMode !== 'file') return fileSummary;
    if (!selectedCategories || selectedCategories.length === 0) return fileSummary;
    if (!Array.isArray(expenses) || expenses.length === 0) return fileSummary;

    let rows = expenses;

    // Filter by selected categories
    const catSet = new Set(selectedCategories);
    rows = rows.filter(exp => catSet.has(exp.data?.subcategory?.category?.name));

    // Respect other global filters (card type, tags)
    if (selectedCardType) {
      rows = rows.filter(exp => (cardTypeMap[exp.upload?.card_type] || exp.upload?.card_type) === selectedCardType);
    }
    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      rows = rows.filter(exp => {
        const tags = getTags(exp);
        return tags.some(t => selectedTags.includes(t));
      });
    }

    const totals = {};
    const labels = {};
    rows.forEach(exp => {
      const ui = expenseUploadInfo.get(exp.id) || getUploadInfo(exp);
      if (!ui || !ui.key) return;
      const amt = Number(exp.amount) || 0;
      totals[ui.key] = (totals[ui.key] || 0) + amt;
      labels[ui.key] = ui.label || String(ui.key);
    });

    return Object.entries(totals).map(([key, amount]) => ({ key, label: labels[key], amount }));
  }, [summaryMode, fileSummary, expenses, selectedCategories, selectedCardType, selectedTags, expenseUploadInfo, getUploadInfo]);

  // Reset filtros cruzados al cambiar de modo
  useEffect(() => {
    if (summaryMode === 'month') { 
      // Keep selectedUploads so the month table can reflect totals by selected files
      setSelectedTags([]);
    } else if (summaryMode === 'file') { 
      setSelectedMonths([]); 
      setSelectedTags([]); 
    } else if (summaryMode === 'tag') { 
      setSelectedMonths([]); 
      // Keep selectedUploads; tag view can also be combined with file if desired
    }
  }, [summaryMode]);

  // Fallback: if backend didn't provide monthly_summary, build it from expenses (for "Todas")
  useEffect(() => {
    if (!expenses.length) return;
    if (baseMonthlySummaryRaw && baseMonthlySummaryRaw.length > 0) return; // already have backend summary

    // Aggregate all expenses by YYYY-MM
    const totals = {};
    expenses.forEach(exp => {
      const m = (exp.date || '').slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(m)) return;
      totals[m] = (totals[m] || 0) + (Number(exp.amount) || 0);
    });
    const fromExpenses = Object.entries(totals)
      .map(([month, amount]) => ({ month, amount: Number(amount) }));

    setBaseMonthlySummaryRaw(fromExpenses);
    if (!selectedCardType) {
      setMonthlySummaryRaw(fromExpenses);
    }
  }, [expenses, baseMonthlySummaryRaw, selectedCardType]);

  // Filtering monthlySummaryRaw after expenses and selectedCardType are loaded/changed
  useEffect(() => {
    // If no card type filter ("Todas"), show the base dashboard summary immediately
    if (!selectedCardType) {
      setMonthlySummaryRaw(baseMonthlySummaryRaw);
      return;
    }

    // If a card type is selected but we don't have expenses yet, keep current view
    if (!expenses.length) return;

    // Filter expenses by the selected card type and recompute the month totals
    const filtered = expenses.filter(exp =>
      (cardTypeMap[exp.upload?.card_type] || exp.upload?.card_type) === selectedCardType
    );
    const monthTotals = {};
    filtered.forEach(exp => {
      const m = (exp.date || '').slice(0, 7); // YYYY-MM
      if (!m) return;
      monthTotals[m] = (monthTotals[m] || 0) + (Number(exp.amount) || 0);
    });
    const newMonthly = Object.entries(monthTotals).map(([month, amount]) => ({ month, amount: Number(amount) }));
    setMonthlySummaryRaw(newMonthly);
  }, [selectedCardType, expenses, baseMonthlySummaryRaw]);

  // Aggregation for category chart (now supports file/tag/month filter)
  useEffect(() => {
    if (!expenses.length) return;

    let filtered = expenses;
    if (summaryMode === 'month') {
      if (selectedMonths.length > 0) {
        filtered = filtered.filter(exp =>
          selectedMonths.some(m => (exp.date || '').startsWith(m))
        );
      }
    } else if (summaryMode === 'file') {
      if (selectedUploads.length > 0) {
        filtered = filtered.filter(exp => {
          const ui = expenseUploadInfo.get(exp.id);
          return ui ? selectedUploads.includes(ui.key) : false;
        });
      }
    }
    if (selectedCardType) {
      filtered = filtered.filter(exp =>
        (cardTypeMap[exp.upload?.card_type] || exp.upload?.card_type) === selectedCardType
      );
    }
    if (selectedTags.length > 0) {
      filtered = filtered.filter(exp => {
        const tags = getTags(exp);
        return tags.some(t => selectedTags.includes(t));
      });
    }

    const totals = {};
    filtered.forEach(exp => {
      const category = exp.data?.subcategory?.category?.name || t('dashboard.uncategorized', 'Sin categoría');
      if (totals[category]) {
        totals[category] += Number(exp.amount);
      } else {
        totals[category] = Number(exp.amount);
      }
    });

    const categoryDataRaw = Object.entries(totals)
      .map(([name, amount]) => ({name, amount: Number(amount)}));
    setCategoryDataRaw(categoryDataRaw);

    // Sort locally for chart labels/values (desc)
    const sortedForChart = Object.entries(totals)
      .map(([name, amount]) => ({ name, amount: Number(amount) }))
      .sort((a, b) => b.amount - a.amount);
    const categoryLabels = sortedForChart.map(c => c.name);
    const categoryValues = sortedForChart.map(c => c.amount);
    const colors = categoryLabels.map(
      (_, i) => `hsl(${(i * 360) / categoryLabels.length}, 50%, 55%)`
    );
    const colorMap = {};
    categoryLabels.forEach((name, i) => {
      colorMap[name] = colors[i];
    });
    setCategoryColorMap(colorMap);

    const categoryData = {
      labels: categoryLabels,
      datasets: [
        {
          data: categoryValues,
          backgroundColor: colors,
        },
      ],
    };
    setCategoryData(categoryData);
  }, [selectedMonths, selectedUploads, summaryMode, expenses, selectedCardType, selectedTags, expenseUploadInfo, t]);

  // Close tag input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If there is no active tag being edited, do nothing
      if (!addingTagId) return;
      // Check if click happened inside a tag anchor
      const tagAnchor = event.target.closest('.tag-anchor');
      if (!tagAnchor) {
        setAddingTagId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addingTagId]);

  const filteredExpenses = React.useMemo(() => {
    if (!Array.isArray(expenses)) return [];
    const q = (debouncedSearch || '').toLowerCase();
    return expenses.filter(exp => {
      const matchesCategory = selectedCategories.length > 0 ? selectedCategories.includes(exp.data?.subcategory?.category?.name) : true;
      const matchesMonth = selectedMonths.length > 0 ? selectedMonths.some(m => (exp.date || '').startsWith(m)) : true;
      const tagsHay = getTags(exp).join(' ').toLowerCase();
      const matchesSearch =
        (exp.description || '').toLowerCase().includes(q) ||
        (exp.data?.subcategory?.category?.name?.toLowerCase() || '').includes(q) ||
        tagsHay.includes(q) ||
        String(Number(exp.amount)).includes(q);
      const matchesCardType = selectedCardType
        ? (cardTypeMap[exp.upload?.card_type] || exp.upload?.card_type) === selectedCardType
        : true;
      let matchesUpload = true;
      if (selectedUploads.length > 0) {
        const ui = expenseUploadInfo.get(exp.id);
        matchesUpload = ui ? selectedUploads.includes(ui.key) : false;
      }
      const matchesTags = selectedTags.length > 0 ? getTags(exp).some(t => selectedTags.includes(t)) : true;
      return matchesCategory && matchesMonth && matchesSearch && matchesCardType && matchesUpload && matchesTags;
    });
  }, [expenses, selectedCategories, selectedMonths, selectedCardType, selectedUploads, selectedTags, debouncedSearch, expenseUploadInfo]);

  const sortedExpenses = React.useMemo(() => {
    const list = [...filteredExpenses];
    return list.sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === 'installmentSortKey') {
        const parseInstallment = (val) => {
          if (!val || typeof val !== 'string' || !val.includes('/')) return -1;
          const [num, total] = val.split('/').map(Number);
          return total ? num / total : -1;
        };
        aVal = parseInstallment(a.installment || a.data?.installment);
        bVal = parseInstallment(b.installment || b.data?.installment);
      } else if (sortColumn === 'destination') {
        aVal = a.location?.name || a.location || a.data?.destination || '';
        bVal = b.location?.name || b.location || b.data?.destination || '';
      } else if (sortColumn === 'category') {
        aVal = a.data?.subcategory?.category?.name || '';
        bVal = b.data?.subcategory?.category?.name || '';
      } else if (sortColumn === 'card_type') {
        aVal = cardTypeMap[a.upload?.card_type] || a.upload?.card_type || '';
        bVal = cardTypeMap[b.upload?.card_type] || b.upload?.card_type || '';
      } else if (sortColumn === 'tags_csv') {
        aVal = (a.data?.tags_csv || '').toString();
        bVal = (b.data?.tags_csv || '').toString();
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (sortColumn === 'amount') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredExpenses, sortColumn, sortDirection]);

  // Calculate filtered total amount for displayed (filtered) expenses
  const filteredTotalAmount = filteredExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);

  // When categories are selected, recompute monthly totals for those categories only
  const monthlySummarySource = React.useMemo(() => {
    // Only affect the Month table; other modes (file/tag) keep their own summaries
    if (summaryMode !== 'month') return monthlySummaryRaw;

    // If no categories selected, use the existing base/filtered monthly summary
    if (!selectedCategories || selectedCategories.length === 0) return monthlySummaryRaw;

    // Need expenses to compute per-category monthly totals
    if (!Array.isArray(expenses) || expenses.length === 0) return monthlySummaryRaw;

    // Start from expenses and apply relevant global filters
    let rows = expenses;

    // Honor card type filter (same as the effect that rebuilds monthlySummaryRaw by card type)
    if (selectedCardType) {
      rows = rows.filter(exp => (cardTypeMap[exp.upload?.card_type] || exp.upload?.card_type) === selectedCardType);
    }

    // If uploads are selected (edge case; usually reset in month mode), respect them
    if (Array.isArray(selectedUploads) && selectedUploads.length > 0) {
      rows = rows.filter(exp => {
        const ui = expenseUploadInfo.get(exp.id);
        return ui ? selectedUploads.includes(ui.key) : false;
      });
    }

    // If tags are selected (usually reset in month mode), respect them
    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      rows = rows.filter(exp => {
        const tags = getTags(exp);
        return tags.some(t => selectedTags.includes(t));
      });
    }

    // Filter by selected categories
    const catSet = new Set(selectedCategories);
    rows = rows.filter(exp => catSet.has(exp.data?.subcategory?.category?.name));

    // Aggregate by YYYY-MM
    const totals = {};
    rows.forEach(exp => {
      const m = (exp.date || '').slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(m)) return;
      totals[m] = (totals[m] || 0) + (Number(exp.amount) || 0);
    });

    return Object.entries(totals).map(([month, amount]) => ({ month, amount: Number(amount) }));
  }, [summaryMode, monthlySummaryRaw, expenses, selectedCategories, selectedCardType, selectedUploads, selectedTags, expenseUploadInfo]);

  const monthlySummarySorted = React.useMemo(() => {
    return [...monthlySummarySource].sort((a, b) => new Date(`${a.month}-01`) - new Date(`${b.month}-01`));
  }, [monthlySummarySource]);

  return (
    <div className="main-content">
      <div className="dashboard-container">
        <Header title={t('dashboard.expenses', 'Expenses')} />

        <div className="dashboard-four-columns">
          <Sidebar />

          {transactionsError &&
              <div className="error-message">
                {transactionsError}
              </div>}

          {!transactionsLoading &&
              <div
                  className="dashboard-right-column"
                  style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem'}}
              >
                {/* Filters row above content */}
                <div className="dashboard-filters-row inside-bar" style={{alignSelf: 'stretch'}}>
                  <div className="dashboard-cardtype-filter">
                    <label className="dashboard-cardtype-label">
                      {t('dashboard.filterByCardType', 'Filter by card type:')}
                    </label>
                    <select
                        className="dashboard-cardtype-select"
                        value={selectedCardType}
                        onChange={(e) => setSelectedCardType(e.target.value)}
                    >
                      <option value="">{t('common.all', 'All')}</option>
                      {cardTypes.map((type, i) => (
                          <option key={i} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="search-input-container embedded">
                    <input
                        type="text"
                        placeholder={t('dashboard.searchPlaceholder', 'Search expenses...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="expense-search-input"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="clear-search-button"
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                        >
                          ×
                        </button>
                    )}
                  </div>
                </div>

                {/* Chart + summary horizontal layout */}
                <div className="dashboard-sections-row" style={{display: 'flex', gap: '1rem', alignItems: 'stretch'}}>
                  <div className="dashboard-section" style={{flex: 1, overflow: 'visible'}}>
                    <h2>{t('dashboard.categories', 'Categories')}</h2>
                    {/* Chart toggle button removed */}
                    <div className="category-chart-container" style={{overflow: 'visible', paddingBottom: '28px'}}>
                      <div className="dashboard-pie-large"
                           style={{maxWidth: '320px', maxHeight: '320px', margin: '0 auto 6px'}}>
                        {categoryData.labels && (
                            <div className="chart-fade-wrapper">
                              <Doughnut data={categoryData} options={options}/>
                            </div>
                        )}
                      </div>
                      <div className="dashboard-categories"
                           style={{fontSize: '16px', lineHeight: 1.45, overflow: 'visible', marginTop: '-8px'}}>
                        {categoryDataSorted.map(({name, amount}) => {
                          const isSelected = selectedCategories.includes(name);
                          const totalCategoryAmount = categoryDataSorted.reduce((sum, c) => sum + Number(c.amount), 0);
                          const percentage = totalCategoryAmount > 0 ? ((amount / totalCategoryAmount) * 100).toFixed(1) : 0;
                          const baseColor = categoryColorMap[name] || 'rgba(0,0,0,0.2)';
                          return (
                              <div
                                  key={name}
                                  className={`category-row ${isSelected ? 'selected' : ''}`}
                                  onClick={() => setSelectedCategories(prev =>
                                      prev.includes(name) ? prev.filter(cat => cat !== name) : [...prev, name]
                                  )}
                                  style={{
                                    cursor: 'pointer',
                                    // Minimal & compact; no solid fill
                                    padding: '2px 6px',
                                    borderRadius: 6,
                                    backgroundColor: 'transparent',
                                    // Slightly thicker left accent when selected
                                    boxShadow: isSelected ? `inset 3px 0 0 0 ${baseColor}` : 'none',
                                    transition: 'box-shadow 120ms ease'
                                  }}
                                  title={t('dashboard.filterByCategoryTooltip', 'Click to filter expenses by this category')}
                              >
                          <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                            <span
                                style={{
                                  display: 'inline-block',
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: baseColor,
                                  boxShadow: isSelected ? `0 0 0 2px #fff, 0 0 0 4px ${baseColor}66` : 'none',
                                  transition: 'box-shadow 120ms ease'
                                }}
                            />
                            <span style={{fontWeight: isSelected ? 600 : 500, lineHeight: 1.2}}>{name}</span>
                          </span>
                                <span
                                    className="category-amount"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'baseline',
                                      gap: 6,
                                      whiteSpace: 'nowrap',
                                      color: 'inherit'
                                    }}
                                >
                            {amount.toLocaleString('es-CL', {style: 'currency', currency: 'CLP'})}
                                  <span
                                      className="category-percent"
                                      style={{opacity: 0.7, fontSize: '12px', color: 'inherit'}}
                                  >
                              ({percentage}%)
                            </span>
                          </span>
                              </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="dashboard-section" style={{flex: 1}}>
                    <h2>{t('dashboard.totals', 'Totales')}</h2>
                    {/* Toggle Month/File */}
                    <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                      <button
                          type="button"
                          className={`pretty-toggle ${summaryMode === 'month' ? 'active' : ''}`}
                          onClick={() => setSummaryMode('month')}
                          aria-pressed={summaryMode === 'month'}
                      >
                        {t('dashboard.monthSummary', 'Por mes')}
                      </button>
                      <button
                          type="button"
                          className={`pretty-toggle ${summaryMode === 'file' ? 'active' : ''}`}
                          onClick={() => setSummaryMode('file')}
                          aria-pressed={summaryMode === 'file'}
                      >
                        {t('dashboard.fileSummary', 'Por archivo')}
                      </button>
                      <button
                          type="button"
                          className={`pretty-toggle ${summaryMode === 'tag' ? 'active' : ''}`}
                          onClick={() => setSummaryMode('tag')}
                          aria-pressed={summaryMode === 'tag'}
                      >
                        {t('dashboard.tagSummary', 'Por etiqueta')}
                      </button>
                    </div>

                    {summaryMode === 'month' ? (
                        <>
                          {(selectedCategories.length > 0 || selectedUploads.length > 0) && (
                              <div className="summary-subtitle" style={{marginBottom: '0.5rem', color: '#64748b'}}>
                                {selectedCategories.length > 0 && (
                                    <span>{t('dashboard.monthSummaryForCategory', 'Por mes — Categoría:')} {selectedCategories.join(', ')}</span>
                                )}
                                {selectedCategories.length > 0 && selectedUploads.length > 0 && ' · '}
                                {selectedUploads.length > 0 && (
                                    <span>{t('dashboard.monthSummaryForFile', 'Por mes — Archivo:')} {selectedUploads.length}</span>
                                )}
                              </div>
                          )}
                          <div style={{display: 'flex', gap: '0.5rem'}}>
                            {[0, 1].map((halfIndex) => {
                              const monthsRaw = monthlySummarySorted.slice(halfIndex * 6, (halfIndex + 1) * 6);
                              const padCount = Math.max(0, 6 - monthsRaw.length);
                              const months = monthsRaw.concat(Array.from({length: padCount}, (_, i) => ({
                                __blank: true,
                                key: `blank-${halfIndex}-${i}`
                              })));

                              return (
                                  <table key={halfIndex} className="dashboard-summary-table" style={{flex: 1}}>
                                    <thead>
                                    <tr>
                                      <th>{t('dashboard.month', 'Month')}</th>
                                      <th>{t('dashboard.amount', 'Amount')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {months.map((row, idx) => {
                                      if (row.__blank) {
                                        return (
                                            <tr key={row.key || `blank-${halfIndex}-${idx}`} className="blank-row"
                                                aria-hidden>
                                              <td>&nbsp;</td>
                                              <td>&nbsp;</td>
                                            </tr>
                                        );
                                      }
                                      const {month, amount} = row;
                                      const isSelected = selectedMonths.includes(month);
                                      return (
                                          <tr
                                              key={month}
                                              className={isSelected ? 'selected' : ''}
                                              aria-selected={isSelected}
                                              onClick={() =>
                                                  setSelectedMonths((prev) =>
                                                      prev.includes(month)
                                                          ? prev.filter((m) => m !== month)
                                                          : [...prev, month]
                                                  )
                                              }
                                              style={{cursor: 'pointer'}}
                                              title={t('dashboard.filterByMonthTooltip', 'Click to filter expenses by this month')}
                                          >
                                            <td className={isSelected ? 'selected-month' : ''}>
                                              {(() => {
                                                const [year, monthNumber] = month.split('-').map(Number);
                                                const date = new Date(year, monthNumber - 1, 1);
                                                const monthShort = date.toLocaleDateString(i18n.resolvedLanguage || 'es-CL', {
                                                  month: 'short',
                                                  timeZone: 'UTC'
                                                }).replace('.', '');
                                                return `${year} ${monthShort.charAt(0).toUpperCase() + monthShort.slice(1)}`;
                                              })()}
                                            </td>
                                            <td>
                                              {amount.toLocaleString('es-CL', {style: 'currency', currency: 'CLP'})}
                                            </td>
                                          </tr>
                                      );
                                    })}
                                    </tbody>
                                  </table>
                              );
                            })}
                          </div>
                        </>
                    ) : summaryMode === 'file' ? (
                        <>
                          {selectedCategories.length > 0 && (
                              <div className="summary-subtitle" style={{marginBottom: '0.5rem', color: '#64748b'}}>
                                {t('dashboard.fileSummaryForCategory', 'Por archivo — Categoría:')} {selectedCategories.join(', ')}
                              </div>
                          )}
                          <div style={{display: 'flex', gap: '0.5rem'}}>
                            {[0, 1].map((halfIndex) => {
                              const files = [...fileSummarySource].sort((a, b) => b.amount - a.amount);
                              const half = Math.ceil(files.length / 2);
                              const slice = files.slice(halfIndex * half, (halfIndex + 1) * half);
                              return (
                                  <table key={halfIndex} className="dashboard-summary-table" style={{flex: 1}}>
                                    <thead>
                                    <tr>
                                      <th>{t('dashboard.file', 'File')}</th>
                                      <th>{t('dashboard.amount', 'Amount')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {slice.length === 0 ? (
                                        <tr>
                                          <td colSpan={2} style={{opacity: 0.7}}>{t('dashboard.noData', 'No data')}</td>
                                        </tr>
                                    ) : (
                                        slice.map(({key, label, amount}) => {
                                          const isSelected = selectedUploads.includes(key);
                                          return (
                                              <tr
                                                  key={key}
                                                  className={isSelected ? 'selected' : ''}
                                                  aria-selected={isSelected}
                                                  onClick={() =>
                                                      setSelectedUploads((prev) =>
                                                          prev.includes(key)
                                                              ? prev.filter((k) => k !== key)
                                                              : [...prev, key]
                                                      )
                                                  }
                                                  style={{cursor: 'pointer'}}
                                                  title={t('dashboard.filterByFileTooltip', 'Click to filter expenses by this file')}
                                              >
                                                <td className={isSelected ? 'selected-month' : ''}>{label}</td>
                                                <td>{amount.toLocaleString('es-CL', {
                                                  style: 'currency',
                                                  currency: 'CLP'
                                                })}</td>
                                              </tr>
                                          );
                                        })
                                    )}
                                    </tbody>
                                  </table>
                              );
                            })}
                          </div>
                        </>
                    ) : (
                        /* Tag table in two columns */
                        <>
                          {selectedCategories.length > 0 && (
                              <div className="summary-subtitle" style={{marginBottom: '0.5rem', color: '#64748b'}}>
                                {t('dashboard.tagSummaryForCategory', 'Por etiqueta — Categoría:')} {selectedCategories.join(', ')}
                              </div>
                          )}
                          <div style={{display: 'flex', gap: '0.5rem'}}>
                            {[0, 1].map((halfIndex) => {
                              const all = [...tagSummary].sort((a, b) => b.amount - a.amount);
                              const half = Math.ceil(all.length / 2);
                              const slice = all.slice(halfIndex * half, (halfIndex + 1) * half);
                              // pad to fixed length to avoid resize when counts differ or are odd
                              const maxRows = half; // fixed per column
                              const padded = slice.concat(
                                  Array.from({length: Math.max(0, maxRows - slice.length)}, (_, i) => ({
                                    tag: `blank-${halfIndex}-${i}`,
                                    amount: null
                                  }))
                              );

                              return (
                                  <table key={halfIndex} className="dashboard-summary-table"
                                         style={{flex: 1, tableLayout: 'fixed', width: '100%'}}>
                                    <thead>
                                    <tr>
                                      <th style={{width: '65%'}}>{t('dashboard.tag', 'Etiqueta')}</th>
                                      <th style={{
                                        width: '35%',
                                        textAlign: 'right'
                                      }}>{t('dashboard.amount', 'Amount')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {padded.length === 0 ? (
                                        <tr>
                                          <td colSpan={2} style={{opacity: 0.7}}>{t('dashboard.noData', 'No data')}</td>
                                        </tr>
                                    ) : (
                                        padded.map(({tag, amount}) => {
                                          if (amount === null) {
                                            return (
                                                <tr key={tag} className="blank-row" aria-hidden>
                                                  <td>&nbsp;</td>
                                                  <td>&nbsp;</td>
                                                </tr>
                                            );
                                          }
                                          const isSelected = selectedTags.includes(tag);
                                          return (
                                              <tr
                                                  key={tag}
                                                  className={isSelected ? 'selected' : ''}
                                                  aria-selected={isSelected}
                                                  onClick={() =>
                                                      setSelectedTags((prev) =>
                                                          prev.includes(tag) ? prev.filter((k) => k !== tag) : [...prev, tag]
                                                      )
                                                  }
                                                  style={{
                                                    cursor: 'pointer',
                                                    transition: 'background-color 120ms ease, box-shadow 120ms ease'
                                                  }}
                                                  title={t('dashboard.filterByTagTooltip', 'Haz clic para filtrar gastos por esta etiqueta')}
                                              >
                                                <td className={isSelected ? 'selected-month' : ''} style={{
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis'
                                                }}>
                                        <span
                                            aria-hidden
                                            style={{
                                              display: 'inline-block',
                                              width: 4,
                                              height: 12,
                                              borderRadius: 2,
                                              backgroundColor: getTagColor(tag),
                                              marginRight: 6,
                                              verticalAlign: 'middle',
                                            }}
                                        />
                                                  {tag}
                                                </td>
                                                <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                                                  {amount.toLocaleString('es-CL', {style: 'currency', currency: 'CLP'})}
                                                </td>
                                              </tr>
                                          );
                                        })
                                    )}
                                    </tbody>
                                  </table>
                              );
                            })}
                          </div>
                        </>
                    )}

                  </div>
                </div>

                {/* Expenses Table Section moved here */}
                <div className="dashboard-section">
                  <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                        marginBottom: hasActiveFilters ? '-10px' : '0.9rem'
                      }}
                  >
                    <h2
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          paddingBottom: 0,
                          borderBottom: 'none'
                        }}
                    >
                      {t('dashboard.expenses', 'Gastos')}
                    </h2>
                    <div
                        className="active-filters"
                        style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: 0}}
                    >
                      {selectedMonths.length > 0 && (
                          <button
                              onClick={() => setSelectedMonths([])}
                              className="clear-filter-button"
                              style={{padding: '4px 10px', fontSize: '13px', lineHeight: 1.25}}
                              title={t('dashboard.clearMonthFilter', 'Clear Month Filter')}
                          >
                            {t('dashboard.month', 'Month')}: {selectedMonths.map(m => {
                            const [year, monthNumber] = m.split('-').map(Number);
                            const date = new Date(year, monthNumber - 1, 1);
                            const monthShort = date.toLocaleDateString(i18n.resolvedLanguage || 'es-CL', {
                              month: 'short',
                              timeZone: 'UTC'
                            }).replace('.', '');
                            return `${year} ${monthShort.charAt(0).toUpperCase() + monthShort.slice(1)}`;
                          }).join(', ')}

                            <span aria-hidden style={{marginLeft: 6}}>×</span>
                          </button>
                      )}
                      {selectedCategories.length > 0 && (
                          <button
                              onClick={() => setSelectedCategories([])}
                              className="clear-filter-button"
                              style={{padding: '4px 10px', fontSize: '13px', lineHeight: '1.25'}}
                              title={t('dashboard.showAllExpenses', 'Show All Expenses')}
                          >
                            {t('dashboard.category', 'Category')}: {selectedCategories.join(', ')}
                            <span aria-hidden style={{marginLeft: 6}}>×</span>
                          </button>
                      )}
                      {selectedUploads.length > 0 && (
                          <button
                              onClick={() => setSelectedUploads([])}
                              className="clear-filter-button"
                              style={{padding: '4px 10px', fontSize: '13px', lineHeight: 1.25}}
                              title={t('dashboard.clearFileFilter', 'Clear File Filter')}
                          >
                            {t('dashboard.file', 'Archivo')}: {selectedUploads
                              .map((key) => {
                                const info = expenseUploadInfo.get(Number(key)) || expenseUploadInfo.get(String(key));
                                return info?.label || key;
                              })
                              .join(', ')}
                            <span aria-hidden style={{marginLeft: 6}}>×</span>
                          </button>
                      )}
                      {selectedTags.length > 0 && (
                          <button
                              onClick={() => setSelectedTags([])}
                              className="clear-filter-button"
                              style={{padding: '4px 10px', fontSize: '13px', lineHeight: 1.25}}
                              title={t('dashboard.clearTagFilter', 'Limpiar filtro de etiqueta')}
                          >
                            {t('dashboard.tags', 'Etiquetas')}: {selectedTags.join(', ')}
                            <span aria-hidden style={{marginLeft: 6}}>×</span>
                          </button>
                      )}
                    </div>
                  </div>
                  {/* Full-width underline below title+chips */}
                  <div style={{
                    borderBottom: '2px solid #3B82F6',
                    width: '100%',
                    marginTop: '2px',
                    marginBottom: '4px'
                  }}/>
                  <div style={{maxHeight: '520px', overflowY: 'auto', borderRadius: '8px'}}>
                    <table className="dashboard-expenses-table">
                      <thead>
                      <tr>
                        <th style={{width: '110px'}} onClick={() => handleSort('date')}>
                          {t('dashboard.date', 'Date')} {sortColumn === 'date' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th
                            style={{
                              maxWidth: '90px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            onClick={() => handleSort('card_type')}
                        >
                          {t('dashboard.cardType', 'Card Type')} {sortColumn === 'card_type' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th
                            style={{
                              maxWidth: '140px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            onClick={() => handleSort('destination')}
                        >
                          {t('dashboard.destination', 'Destination')} {sortColumn === 'destination' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th onClick={() => handleSort('description')}>{t('dashboard.description', 'Description')} {sortColumn === 'description' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                        <th
                            onClick={() => handleSort('category')}
                            style={{paddingLeft: '36px'}}
                        >
                          {t('dashboard.category', 'Category')} {sortColumn === 'category' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th onClick={() => handleSort('tags_csv')}>
                          {t('dashboard.tags', 'Etiquetas')} {sortColumn === 'tags_csv' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th className="amount-column"
                            onClick={() => handleSort('amount')}>{t('dashboard.amount', 'Amount')} {sortColumn === 'amount' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                        <th className="amount-column" style={{width: '90px'}}
                            onClick={() => handleSort('installmentSortKey')}>
                          {t('dashboard.installments', 'Installments')} {sortColumn === 'installmentSortKey' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      {sortedExpenses.map((expense) => (
                          <tr
                              key={expense.id ?? expense.reference_code ?? expense.description}
                              className={addingTagId === expense.id ? 'is-editing-tag' : ''}
                              onClick={() => setSelectedExpense(expense)}
                              style={{cursor: 'pointer'}}
                          >
                            <td style={{width: '110px'}}>{toYMD(expense.date)}</td>
                            <td
                                style={{
                                  maxWidth: '90px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                            >
                              {cardTypeMap[expense.upload?.card_type] || expense.upload?.card_type || 'N/A'}
                            </td>
                            <td
                                style={{
                                  maxWidth: '140px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                            >
                              {expense.location?.name || expense.location || expense.data?.destination || ''}
                            </td>
                            <td>
                              <div>{expense.description}</div>
                            </td>
                            <td className="category-cell" style={{position: 'relative', overflow: 'visible'}}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 8,
                                paddingLeft: '20px'
                              }}>
                          <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                            <span
                                style={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: categoryColorMap[expense.data?.subcategory?.category?.name] || 'rgba(0,0,0,0.2)',
                                }}
                            />
                            {expense.data?.subcategory?.category?.name || t('dashboard.uncategorized', 'Sin categoría')}
                          </span>
                              </div>
                            </td>
                            <td className="tags-cell"
                                style={{position: 'relative', overflow: 'visible', minWidth: 300}}>
                              <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 6,
                                    rowGap: 6,
                                  }}
                              >
                                {getTags(expense).map(tag => (
                                    <span
                                        key={tag}
                                        className="expense-tag"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          border: '1px solid rgba(0,0,0,0.12)',
                                          borderRadius: 6,
                                          backgroundColor: 'rgba(245, 247, 250, 0.9)',
                                          color: '#333',
                                          padding: '3px 8px',
                                          fontSize: '12px',
                                          fontWeight: 500,
                                          lineHeight: 1.2,
                                          maxWidth: '100%',
                                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                                        }}
                                    >
                              <span
                                  style={{
                                    display: 'inline-block',
                                    width: 4,
                                    height: 12,
                                    backgroundColor: getTagColor(tag),
                                    borderRadius: 2,
                                  }}
                              />
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>{tag}</span>
                              <button
                                  type="button"
                                  className="tag-remove-button"
                                  title={t('dashboard.removeTag', 'Eliminar etiqueta')}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTagFromExpense(expense.id, tag);
                                  }}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: '2px 4px',
                                    color: 'rgba(60,60,60,0.75)',
                                    fontSize: '15px',
                                    borderRadius: '50%',
                                    lineHeight: 1,
                                    transition: 'background-color 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  aria-label={t('dashboard.removeTag', 'Eliminar etiqueta')}
                              >
                                ×
                              </button>
                            </span>
                                ))}

                                {/* Inline add-tag control at the end of the chips */}
                                {addingTagId === expense.id ? (
                                    <span className="tag-anchor" style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      position: 'relative'
                                    }}>
                              <input
                                  type="text"
                                  autoFocus
                                  style={{
                                    border: '1px dashed rgba(0,0,0,0.25)',
                                    borderRadius: 8,
                                    outline: 'none',
                                    background: 'transparent',
                                    padding: '2px 6px',
                                    width: '120px',
                                    fontSize: '0.95rem'
                                  }}
                                  placeholder={t('dashboard.addTagPlaceholder', 'Etiqueta')}
                                  value={expense.newTag || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setExpenses(prev =>
                                        prev.map(exp => exp.id === expense.id ? {...exp, newTag: val} : exp)
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && expense.newTag) {
                                      const busy = inFlightTagAddRef.current.get(String(expense.id));
                                      if (!busy) {
                                        addTagToExpense(expense.id, expense.newTag);
                                      }
                                      setExpenses(prev => prev.map(x => x.id === expense.id ? {...x, newTag: ''} : x));
                                      setAddingTagId(null);
                                    } else if (e.key === 'Escape') {
                                      setExpenses(prev => prev.map(x => x.id === expense.id ? {...x, newTag: ''} : x));
                                      setAddingTagId(null);
                                    }
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                              />
                              <TagSuggestions
                                  query={expense.newTag}
                                  exclude={getTags(expense)}
                                  all={allTags}
                                  onPick={(tag) => {
                                    const busy = inFlightTagAddRef.current.get(String(expense.id));
                                    if (!busy) {
                                      addTagToExpense(expense.id, tag);
                                    }
                                    setExpenses(prev => prev.map(x => x.id === expense.id ? {...x, newTag: ''} : x));
                                    setAddingTagId(null);
                                  }}
                                  counts={tagUsageCount}
                              />
                            </span>
                                ) : (
                                    <button
                                        className={`add-tag-button ${addingTagId === expense.id ? '' : 'hover-visible'}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (addingTagId !== expense.id) {
                                            setExpenses(prev => prev.map(x => x.id === expense.id ? {
                                              ...x,
                                              newTag: ''
                                            } : x));
                                            setAddingTagId(expense.id);
                                          }
                                        }}
                                        title={t('dashboard.addTag', 'Agregar etiqueta')}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px dashed rgba(0,0,0,0.25)',
                                          borderRadius: 12,
                                          background: 'transparent',
                                          padding: '2px 8px',
                                          lineHeight: 1.2,
                                          cursor: 'pointer',
                                          whiteSpace: 'nowrap',
                                          flex: '0 0 auto',
                                        }}
                                    >
                                      +
                                    </button>
                                )}
                              </div>
                            </td>
                            <td className="amount-column">{Number(expense.amount).toLocaleString('es-CL', {
                              style: 'currency',
                              currency: 'CLP'
                            })}</td>
                            <td className="amount-column" style={{width: '90px'}}>
                              {expense.installment || expense.data?.installment || '–'}
                            </td>
                          </tr>
                      ))}
                      </tbody>
                      <tfoot>
                      <tr className="summary-row">
                        <td colSpan={5}>{t('common.total', 'Total')}</td>
                        <td></td>
                        <td className="amount-column">{filteredTotalAmount.toLocaleString('es-CL', {
                          style: 'currency',
                          currency: 'CLP'
                        })}</td>
                        <td></td>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {selectedExpense && (
                    <div className="expense-popup-overlay" onClick={() => setSelectedExpense(null)}>
                      <div className="expense-popup" onClick={(e) => e.stopPropagation()}>
                        <h3>{t('dashboard.expenseDetail', 'Expense Detail')}</h3>
                        <p><strong>{t('dashboard.date', 'Date')}:</strong> {toYMD(selectedExpense.date)}</p>
                        <p>
                          <strong>{t('dashboard.destination', 'Destination')}:</strong> {selectedExpense.location?.name || selectedExpense.location || selectedExpense.data?.destination || ''}
                        </p>
                        <p><strong>{t('dashboard.description', 'Description')}:</strong> {selectedExpense.description}
                        </p>
                        <p>
                          <strong>{t('dashboard.category', 'Category')}:</strong> {selectedExpense.data?.subcategory?.category?.name || t('dashboard.uncategorized', 'Sin categoría')}
                        </p>
                        <p>
                          <strong>{t('dashboard.subcategory', 'Subcategory')}:</strong> {selectedExpense.data?.subcategory?.name || 'N/A'}
                        </p>
                        <p>
                          <strong>{t('dashboard.amount', 'Amount')}:</strong> {Number(selectedExpense.amount).toLocaleString('es-CL', {
                          style: 'currency',
                          currency: 'CLP'
                        })}</p>
                        <p>
                          <strong>{t('dashboard.installments', 'Installments')}:</strong> {selectedExpense.installment || selectedExpense.data?.installment || 'N/A'}
                        </p>
                        <p>
                          <strong>{t('dashboard.referenceCode', 'Reference Code')}:</strong> {selectedExpense.reference_code || 'N/A'}
                        </p>
                        <p><strong>{t('dashboard.type', 'Type')}:</strong> {selectedExpense.type || 'N/A'}</p>
                        <p>
                          <strong>{t('dashboard.card', 'Card')}:</strong> {cardTypeMap[selectedExpense.upload?.card_type] || selectedExpense.upload?.card_type || 'N/A'}
                        </p>
                        <p><strong>{t('dashboard.bank', 'Bank')}:</strong> {selectedExpense.upload?.bank?.name || 'N/A'}
                        </p>
                        <p>
                          <strong>{t('dashboard.file', 'File')}:</strong> {resolveFilename(selectedExpense.upload || {}) || selectedExpense.upload?.filename || selectedExpense.upload_filename || 'N/A'}
                        </p>
                        <p>
                          <strong>{t('dashboard.tags', 'Etiquetas')}:</strong>{' '}
                          {getTags(selectedExpense).length > 0 ? (
                              getTags(selectedExpense).map(tag => (
                                  <span
                                      key={tag}
                                      className="expense-tag"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        borderRadius: 6,
                                        backgroundColor: 'rgba(245, 247, 250, 0.9)',
                                        color: '#333',
                                        padding: '3px 8px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                        marginRight: 4,
                                        marginBottom: 4,
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                                      }}
                                  >
                          <span
                              style={{
                                display: 'inline-block',
                                width: 4,
                                height: 12,
                                backgroundColor: getTagColor(tag),
                                borderRadius: 2,
                              }}
                          />
                          <span
                              style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{tag}</span>
                          <button
                              type="button"
                              className="tag-remove-button"
                              title={t('dashboard.removeTag', 'Eliminar etiqueta')}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTagFromExpense(selectedExpense.id, tag);
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                color: 'rgba(60,60,60,0.75)',
                                fontSize: '15px',
                                borderRadius: '50%',
                                lineHeight: 1,
                                transition: 'background-color 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              aria-label={t('dashboard.removeTag', 'Eliminar etiqueta')}
                          >
                            ×
                          </button>
                        </span>
                              ))
                          ) : (
                              <span style={{opacity: 0.6}}>{t('dashboard.noTags', 'Sin etiquetas')}</span>
                          )}
                        </p>
                        <div className="add-tag-container" style={{position: 'relative'}}>
                          <input
                              type="text"
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              placeholder={t('dashboard.addTagPlaceholder', 'Agregar una etiqueta...')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') addTagToExpense(selectedExpense.id, newTag);
                              }}
                          />
                          <TagSuggestions
                              query={newTag}
                              exclude={getTags(selectedExpense)}
                              all={allTags}
                              onPick={(tag) => addTagToExpense(selectedExpense.id, tag)}
                              showOnEmpty={false}
                              counts={tagUsageCount}
                          />
                          <button onClick={() => addTagToExpense(selectedExpense.id, newTag)}>
                            {t('dashboard.addTag', 'Agregar etiqueta')}
                          </button>
                        </div>
                        <button onClick={() => setSelectedExpense(null)}>{t('common.close', 'Close')}</button>
                      </div>
                    </div>
                )}
              </div>}
        </div> {/* cierre de dashboard-four-columns */}
      </div>
    </div>
  );
};

export default Expenses;